/**
 * Wire contracts for the inspector payout-card binding flow (AUT-CARDS-*).
 *
 * An `InspectorCard` is the persistent payout authorisation vended by
 * WebPay's «упрощённый платёж»: the inspector binds a Belarusian-issued
 * Visa / Mastercard / БЕЛКАРТ by paying 0.10 BYN (a real capture, refunded
 * by us straight after), and the resulting `bindTransactionId` — the
 * payment's `transaction_id`, used as `wsb_token_p2p` for МТБанк P2P
 * payouts — lives on the `inspector_cards` row. Up to 3 active cards per
 * inspector; one `isPrimary` for default payout selection.
 *
 * AUT-229: this used to ride `recurring_bind`, which the vendor has
 * disabled on both our merchant accounts. `recurringToken` is now always
 * null server-side.
 *
 * This module owns the wire-stable shape exposed to mobile (own-inspector
 * "Привязанные карты" screen, BindCard flow, Earnings withdraw) and admin
 * (force-unbind monitoring). The `recurringToken` and `bindTransactionId`
 * columns NEVER cross the wire — only the masked PAN (`last4`), brand, and
 * holder name. `bindTransactionId` is password-class: it authorises real
 * transfers to the inspector's card. Audit logs use a SHA-256 head of it
 * for correlation.
 *
 * Centralised in `@carcheck/shared` to eliminate the AUT-39-style
 * api↔mobile mirror drift; both packages import the same source of
 * truth. Mirror any addition to `InspectorCardStatus` in the API-side
 * Prisma enum + the Zod schema in `inspectors-cards/dto/` (AUT-91)
 * to keep the narrowing test catching drift.
 *
 * Date fields are ISO-8601 strings (JSON has no `Date`); consumers
 * convert at the render boundary via `new Date(dto.createdAt)`.
 */
/**
 * Lifecycle of an `InspectorCard` row.
 *
 * - `binding`   — row created, awaiting the WebPay bind notifier (max
 *                 20 min before `cards-bind-timeout` job flips to
 *                 `abandoned`)
 * - `active`    — notifier confirmed, `bindTransactionId` persisted, card
 *                 usable for МТБанк P2P payouts
 * - `expired`   — card's `expiresAt` reached; `cards-expiry-watcher`
 *                 cron flips on the day of expiry
 * - `unbound`   — inspector or admin explicitly removed the binding. The
 *                 flip is LOCAL: no vendor revocation call is reachable,
 *                 so `bindTransactionId` is nulled instead (AUT-229)
 * - `abandoned` — bind WebView never returned; auto-timed-out at 20 min
 * - `suspended` — inspector deactivated by admin (AUT-88 lifecycle event)
 * - `failed`    — bind declined by WebPay or acquirer (holder mismatch,
 *                 foreign issuer, declined card, etc.)
 */
export const InspectorCardStatus = {
    Binding: 'binding',
    Active: 'active',
    Expired: 'expired',
    Unbound: 'unbound',
    Abandoned: 'abandoned',
    Suspended: 'suspended',
    Failed: 'failed',
};
export const INSPECTOR_CARD_STATUS_VALUES = Object.values(InspectorCardStatus);
export function isInspectorCardStatus(value) {
    return (typeof value === 'string' && INSPECTOR_CARD_STATUS_VALUES.includes(value));
}
//# sourceMappingURL=inspector-card.js.map